import React, { useState, useEffect } from "react";
import "./css/LoginFormModal.css";
import MfaAPI from "../services/MfaAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faShieldAlt,
  faSync,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Modal for MFA verification during login
 */
function MfaVerificationModal({
  sessionId,
  mfaMethod,
  onVerified,
  onCancel,
  onResendCode,
}) {
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [canResend, setCanResend] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-theme") || "light"
  );

  // Detect theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const currentTheme =
        document.documentElement.getAttribute("data-theme") || "light";
      setTheme(currentTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");

    const codeToVerify = useBackupCode ? backupCode.trim() : code.trim();

    if (!codeToVerify) {
      setError(
        useBackupCode
          ? "Please enter a backup code"
          : "Please enter the verification code"
      );
      return;
    }

    setIsVerifying(true);

    try {
      const response = await MfaAPI.verifyMfaLogin(
        sessionId,
        useBackupCode ? null : codeToVerify,
        useBackupCode ? codeToVerify : null
      );

      setIsVerifying(false);
      // Call onVerified with the response data
      onVerified(response);
    } catch (err) {
      setIsVerifying(false);

      // Handle error response - APIService returns err.error
      if (err.error) {
        setError(err.error || "Verification failed");

        if (err.attemptsRemaining !== undefined) {
          setAttemptsRemaining(err.attemptsRemaining);
        }
      } else {
        setError("Verification failed. Please try again.");
      }

      // Clear the code input
      if (useBackupCode) {
        setBackupCode("");
      } else {
        setCode("");
      }
    }
  };

  const handleResendCode = async () => {
    if (!canResend || resendCooldown > 0) return;

    setCanResend(false);
    setResendCooldown(30); // 30 second cooldown
    setError("");

    try {
      await onResendCode();
    } catch (err) {
      setError("Failed to resend code. Please try again.");
    }
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setError("");
    setCode("");
    setBackupCode("");
  };

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="login-modal" onClick={handleOutsideClick}>
      <div
        className="login-modal-content"
        data-theme={theme}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close" onClick={onCancel} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2>
          <FontAwesomeIcon
            icon={faShieldAlt}
            style={{ marginRight: "0.5rem" }}
          />
          Two-Factor Authentication
        </h2>

        {!useBackupCode ? (
          <>
            <p style={{ marginBottom: "0.5rem", textAlign: "center" }}>
              {mfaMethod === "email"
                ? "A verification code has been sent to your registered email address"
                : "A verification code has been sent to your registered phone number"}
            </p>
            <p
              style={{
                marginBottom: "1.5rem",
                textAlign: "center",
                fontSize: "0.9rem",
                opacity: 0.8,
              }}
            >
              Enter the 6-digit code to continue
            </p>
          </>
        ) : (
          <>
            <p style={{ marginBottom: "0.5rem", textAlign: "center" }}>
              Enter one of your backup codes
            </p>
            <p
              style={{
                marginBottom: "1.5rem",
                textAlign: "center",
                fontSize: "0.9rem",
                opacity: 0.8,
              }}
            >
              Each backup code can only be used once
            </p>
          </>
        )}

        <form onSubmit={handleVerify}>
          <div className="form-group">
            {!useBackupCode ? (
              <input
                type="text"
                maxLength="6"
                placeholder="000000"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setCode(value);
                  setError("");
                }}
                style={{
                  fontSize: "1.5rem",
                  textAlign: "center",
                  letterSpacing: "0.5rem",
                  fontFamily: "monospace",
                  padding: "1rem",
                }}
                disabled={isVerifying}
                autoComplete="off"
                autoFocus
              />
            ) : (
              <input
                type="text"
                maxLength="8"
                placeholder="XXXXXXXX"
                value={backupCode}
                onChange={(e) => {
                  setBackupCode(e.target.value.toUpperCase());
                  setError("");
                }}
                style={{
                  fontSize: "1.25rem",
                  textAlign: "center",
                  letterSpacing: "0.3rem",
                  fontFamily: "monospace",
                  padding: "1rem",
                }}
                disabled={isVerifying}
                autoComplete="off"
                autoFocus
              />
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          {attemptsRemaining < 3 && attemptsRemaining > 0 && (
            <div className="warning-message">
              {attemptsRemaining}{" "}
              {attemptsRemaining === 1 ? "attempt" : "attempts"} remaining
            </div>
          )}

          <button
            type="submit"
            className="common-button primary"
            style={{ width: "100%", marginTop: "1rem" }}
            disabled={isVerifying}
          >
            {isVerifying ? "Verifying..." : "Verify"}
          </button>
        </form>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginTop: "1.5rem",
            paddingTop: "1rem",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          {!useBackupCode ? (
            <>
              <button
                type="button"
                className="link-button"
                onClick={handleResendCode}
                disabled={!canResend || resendCooldown > 0}
                style={{ textAlign: "center" }}
              >
                <FontAwesomeIcon
                  icon={faSync}
                  style={{ marginRight: "0.25rem" }}
                />
                {resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : "Resend code"}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={toggleBackupCode}
                style={{ textAlign: "center" }}
              >
                Use backup code instead
              </button>
            </>
          ) : (
            <button
              type="button"
              className="link-button"
              onClick={toggleBackupCode}
              style={{ textAlign: "center" }}
            >
              Use verification code instead
            </button>
          )}
        </div>

        <p
          style={{
            textAlign: "center",
            fontSize: "0.85rem",
            marginTop: "1rem",
            opacity: 0.7,
          }}
        >
          Code can take up to 30 seconds to arrive
        </p>
      </div>
    </div>
  );
}

export default MfaVerificationModal;
